<template>
  <!-- View a finished assessment of an asset: the business impact result and the answers, in a side panel (US 9370).
       Used on the Assessments tab of systems, processes, activities and contracts. -->
  <span>
    <button v-if="button" type="button" class="btn btn-view" :aria-label="$t('assessment_view.open')" qid="assessment-view-open" @click="show()">
      <font-awesome-icon icon="eye"/>
    </button>

    <V2SidePanel ref="panel"
                 :kind="$t('assessment_view.kind')"
                 :title="selected ? selected.title : ''"
                 :intro="intro"
                 qid="assessment-view-panel">
      <template v-if="selected">
        <div class="av-meta">
          <router-link :to="{ name: 'projects.view', params: { slug: selected.slug } }" class="v2-link" qid="assessment-view-project">
            <font-awesome-icon icon="file-alt"/>{{ $t('assessment_view.open_project') }}
          </router-link>
          <span v-if="selected.assessment" class="sp-chip">{{ selected.assessment.name }}</span>
          <span v-if="!selected.assessment || selected.assessment.name !== typeLabel" class="sp-chip">{{ typeLabel }}</span>
        </div>

        <!-- Business impact: one result per asset in scope -->
        <section v-for="result in impacts" :key="'impact' + result.id" class="av-impact" qid="assessment-view-impact">
          <h3 class="av-h">
            <font-awesome-icon icon="chart-line"/>{{ $t('assessment_view.impact_title') }}
            <span v-if="result.scope && impacts.length > 1" class="av-h__sub">· {{ result.scope.name }}</span>
          </h3>
          <div class="av-cia">
            <div v-for="key in ['confidentiality', 'integrity', 'availability']" :key="key" class="av-cia__item">
              <b :class="impactClass(result[key])">{{ impactLevel(result[key]) || '–' }}</b>
              <span>
                {{ $t('systems_overview.criticality.' + key) }}
                <small>{{ result[key] ? $t('impact.' + impactKey(result[key])) : $t('assessment_view.not_answered') }}</small>
              </span>
            </div>
          </div>
          <div class="av-rows">
            <div class="sp-row"><span>{{ $t('system_security.rto') }}</span><b>{{ result.rto_number ? result.rto_number + ' ' + result.rto_type : '–' }}</b></div>
            <div class="sp-row"><span>{{ $t('system_security.rpo') }}</span><b>{{ result.rpo_number ? result.rpo_number + ' ' + result.rpo_type : '–' }}</b></div>
          </div>
          <div v-if="result.remarks" class="av-note"><FormattedNoteText :value="result.remarks"/></div>
        </section>

        <!-- Questions and answers -->
        <section v-if="tasks.length" class="av-questions">
          <h3 class="av-h">
            <font-awesome-icon icon="list-ul"/>{{ $t('assessment_view.questions_title') }}
            <span class="av-h__sub">· {{ answeredText }}</span>
          </h3>
          <div v-for="task in tasks" :key="task.id" class="av-q" :qid="'assessment-view-question-' + task.id">
            <div class="av-q__top">
              <span class="av-q__title">{{ task.title }}</span>
              <span v-if="task.response" class="sx-st" :class="'is-' + statusOf(task)">
                <font-awesome-icon v-if="statusIcon[statusOf(task)]" :icon="statusIcon[statusOf(task)]"/>
                <template v-if="task.response_type === 'Datepicker'">{{ task.response | formatDate }}</template>
                <template v-else>{{ task.response | capitalize }}</template>
              </span>
              <span v-else class="sx-st is-unanswered">{{ $t('assessment_view.not_answered') }}</span>
            </div>
            <div v-if="task.remarks" class="av-note"><FormattedNoteText :value="task.remarks"/></div>
            <div v-if="task.control_description" class="av-note"><FormattedNoteText :value="task.control_description"/></div>
            <div class="av-q__meta">
              <span v-if="task.users && task.users.length" class="av-q__people">
                <Avatar v-for="user in task.users" :key="user.id" :object="user" size="xs"></Avatar>
              </span>
              <span v-if="attachments(task)"><font-awesome-icon icon="paperclip"/>{{ $tc('assessment_view.attachments', attachments(task), { count: attachments(task) }) }}</span>
              <span>{{ $t('assessment_view.updated', { date: formatDate(task.updated_at) }) }}</span>
            </div>
          </div>
        </section>

        <p v-if="!loading && !tasks.length && !impacts.length" class="sx-empty">{{ $t('assessment_view.empty') }}</p>
        <p v-if="loading" class="sx-empty"><font-awesome-icon icon="spinner" class="fa-spin"/></p>
      </template>

      <template #foot>
        <span class="v2-panel__grow"></span>
        <b-button variant="secondary" @click="$refs.panel.hide()">{{ $t('system.close') }}</b-button>
      </template>
    </V2SidePanel>
  </span>
</template>

<script>
  import moment from 'moment';
  import piincHttp from '@/util/configureAxios.js';
  import Avatar from '@/components/Avatar.vue';
  import V2SidePanel from '@/components/V2SidePanel.vue';
  import FormattedNoteText from '@/components/tasks/FormattedNoteText.vue';
  import {impactClass, impactLevel, impactKey} from '@/util/impactLevels.js';

  // Project types whose questions are about one asset; the others list all the project's tasks
  const PER_ASSET = ['risk_assessment', 'bia_assessment'];

  export default {
    name: 'AssessmentViewPanel',
    components: {
      Avatar,
      V2SidePanel,
      FormattedNoteText
    },
    props: {
      // The asset the assessment is shown for (system, process, activity or contract)
      object: { type: Object, required: true },
      // With a project, the panel draws its own eye button for it
      project: { type: Object, default: null },
      button: { type: Boolean, default: true }
    },
    data: function() {
      return {
        selected: null,
        tasks: [],
        impacts: [],
        loading: false,
        statusIcon: { in_place: 'check', not_in_place: 'times', partly: 'info', unanswered: 'clock' }
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      intro() {
        if (!this.selected) {
          return '';
        }
        let date = this.selected.completed_at || this.selected.updated_at;
        return this.$t('assessment_view.intro', { name: this.object.name, date: this.formatDate(date) });
      },
      typeLabel() {
        let type = this.selected.type;
        return this.$te('assessment_view.types.' + type) ? this.$t('assessment_view.types.' + type) : type;
      },
      answeredText() {
        let answered = this.tasks.filter(task => task.response).length;
        return this.$t('assessment_view.answered', { count: answered, total: this.tasks.length });
      }
    },
    methods: {
      impactClass: impactClass,
      impactLevel: impactLevel,
      impactKey: impactKey,
      show(project) {
        this.selected = project || this.project;
        this.tasks = [];
        this.impacts = [];
        this.load();
        this.$refs.panel.show();
      },
      formatDate(value) {
        return value ? moment(value).format('D MMM YYYY') : '';
      },
      // Response statuses from the assessment template, shown with the same colours as measures
      statusOf(task) {
        let status = task.response_status;
        if (status === 'positive') {
          return 'in_place';
        }
        if (status === 'negative') {
          return 'not_in_place';
        }
        if (status === 'pending') {
          return 'unanswered';
        }
        return 'partly';
      },
      attachments(task) {
        return ((task.media && task.media.length) || 0) + ((task.links && task.links.length) || 0);
      },
      load() {
        let self = this;
        let project = this.selected;
        let perAsset = PER_ASSET.includes(project.type);
        let params = { client: this.getClient.slug, per_page: 100 };
        let asset = { model_type: this.object.who_am_i, model_id: this.object.id };

        this.loading = true;
        let questions = piincHttp.get('projects/' + project.slug + '/tasks', { params: perAsset
          ? Object.assign({ not_code: 'system_impact_assessment' }, params, asset)
          : Object.assign({ not_code: 'update_measures' }, params) });
        let impacts = perAsset
          ? piincHttp.get('projects/' + project.slug + '/tasks', { params: Object.assign({ code: 'system_impact_assessment' }, params, asset) })
          : Promise.resolve({ data: { items: [] } });

        Promise.all([questions, impacts]).then(function(responses) {
          self.tasks = responses[0].data.items;
          self.impacts = responses[1].data.items;
          self.loading = false;
        }, function() {
          self.loading = false;
        });
      }
    }
  }
</script>
